<template>
    <div @click="closeContextMenu" @contextmenu.prevent>
        <div v-if="!draggable" class="overflow-x-auto">
            <table
                :class="[
                    'min-w-full divide-y divide-theme-border bg-theme-card text-theme-base',
                    outlined ? 'border border-theme-border' : ''
                ]"
            >
                <thead class="bg-theme-hover">
                    <tr>
                        <th
                            v-for="field in fields"
                            :key="field.key"
                            :class="[
                                'xl:px-4 lg:px-3 px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-theme-base',
                                field.thClass,
                                { 'sortable-header': field.sortable }
                            ]"
                            :role="field.sortable ? 'columnheader' : null"
                            :scope="'col'"
                            :tabindex="field.sortable ? '0' : null"
                            :aria-colindex="field.colIndex"
                            :aria-sort="getAriaSort(field.key)"
                            @click="
                                field.sortable ? toggleSort(field.key) : null
                            "
                            @keydown.enter="
                                field.sortable ? toggleSort(field.key) : null
                            "
                            @keydown.space.prevent="
                                field.sortable ? toggleSort(field.key) : null
                            "
                        >
                            <div class="flex items-center justify-between">
                                <span class="text-bold">{{ field.label }}</span>
                                <span
                                    v-if="field.sortable"
                                    class="sort-icons ml-1"
                                    :class="{ active: sortBy === field.key }"
                                >
                                    <span
                                        v-if="sortBy !== field.key"
                                        class="sort-icon-none"
                                    >
                                        ⇅
                                    </span>
                                    <span
                                        v-else-if="sortDesc"
                                        class="sort-icon-desc"
                                    >
                                        ▼
                                    </span>
                                    <span v-else class="sort-icon-asc">
                                        ▲
                                    </span>
                                </span>
                            </div>
                            <span v-if="field.sortable" class="sr-only">
                                {{ getSrOnlyText(field.key) }}
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(item, index) in sortedItems"
                        :key="index"
                        :class="[
                            'h-14 bg-table-row-theme hover:bg-theme-hover',
                            {
                                'border-2 bg-theme-selected border-theme-primary':
                                    selectedIndex.includes(index)
                            },
                            selectable ? 'cursor-pointer' : ''
                        ]"
                        @click="selectable ? selectRow(item, index) : null"
                        @contextmenu.prevent.stop="
                            openContextMenu($event, item, index)
                        "
                    >
                        <td
                            v-for="field in fields"
                            :key="field.key"
                            :class="[
                                'xl:px-4 lg:px-3 px-2 py-2 whitespace-nowrap text-sm text-theme-base',
                                field.tdClass
                            ]"
                        >
                            <slot
                                :name="`cell(${field.key})`"
                                :item="item"
                                :index="index"
                                :value="item[field.key]"
                            >
                                {{ item[field.key] }}
                            </slot>
                        </td>
                    </tr>
                    <tr v-if="sortedItems.length === 0">
                        <td
                            :colspan="fields.length"
                            class="text-center py-4 text-sm text-theme-base"
                        >
                            {{ itemsEmptyMessage }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- ── Draggable table (draggable = true) ── -->
        <div v-else class="overflow-x-auto w-full">
            <div class="text-sm min-w-max bg-theme-card text-theme-base">
                <div class="bg-theme-hover text-theme-base flex">
                    <div
                        class="shrink-0 px-3 py-4 text-center text-xs font-semibold uppercase w-16 flex items-center"
                    >
                        Mover
                    </div>

                    <div
                        v-for="field in fields"
                        :key="field.key"
                        :class="[
                            'shrink-0 px-3 py-4 text-left text-xs font-semibold uppercase flex items-center justify-center',
                            field.thClass
                        ]"
                    >
                        {{ field.label }}
                    </div>
                </div>

                <Container
                    tag="div"
                    drag-handle-selector=".drag-handle"
                    @drop="onDrop"
                >
                    <Draggable
                        v-for="(item, index) in localItems"
                        :key="item.id || index"
                        tag="div"
                    >
                        <div
                            :class="[
                                'flex hover:bg-opacity-50 hover:bg-theme-hover',
                                {
                                    'border-2 bg-theme-selected border-theme-primary':
                                        contextMenu.rowIndex === index
                                }
                            ]"
                            @contextmenu.prevent.stop="
                                openContextMenu($event, item, index)
                            "
                        >
                            <div
                                class="shrink-0 px-3 py-2 text-center drag-handle cursor-grab select-none text-xl w-16"
                            >
                                ⠿
                            </div>

                            <div
                                v-for="field in fields"
                                :key="field.key"
                                :class="[
                                    'shrink-0 px-3 py-2 text-sm',
                                    field.tdClass
                                ]"
                            >
                                <div class="truncate">
                                    <slot
                                        :name="`cell(${field.key})`"
                                        :item="item"
                                        :index="index"
                                        :value="item[field.key]"
                                    >
                                        {{ item[field.key] }}
                                    </slot>
                                </div>
                            </div>
                        </div>
                    </Draggable>
                </Container>
            </div>
        </div>

        <!-- ── Context menu ── -->
        <div
            v-if="contextMenu.visible"
            v-click-outside="closeContextMenu"
            class="fixed z-50 rounded shadow-lg border border-brand-blue bg-theme-card"
            :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
            @click.stop
            @contextmenu.prevent.stop
        >
            <ul class="py-1 min-w-max">
                <fragment v-if="!draggable && rowActions.length">
                    <li v-for="action in rowActions" :key="action.key">
                        <button
                            class="w-full text-left px-4 py-2 text-sm text-theme-base hover:bg-theme-card"
                            @click="emitRowAction(action.key, contextMenu.item)"
                        >
                            {{ action.icon }} {{ action.label }}
                        </button>
                    </li>
                    <li class="border-t border-brand-blue mx-2 my-1"></li>
                </fragment>
                <fragment v-if="draggable">
                    <li>
                        <button
                            class="w-full text-left px-4 py-2 text-sm text-theme-base hover:bg-theme-card disabled:opacity-40 disabled:cursor-not-allowed"
                            :disabled="effectiveCurrentPage === 1"
                            @click="goToFirstPage"
                        >
                            ⏮ Enviar a primera página
                        </button>
                    </li>
                    <li>
                        <button
                            class="w-full text-left px-4 py-2 text-sm text-theme-base hover:bg-theme-card disabled:opacity-40 disabled:cursor-not-allowed"
                            :disabled="effectiveCurrentPage === 1"
                            @click="goToPrevPage"
                        >
                            ◀ Enviar a página anterior
                        </button>
                    </li>
                    <li class="border-t border-brand-blue mx-2 my-1"></li>
                    <li>
                        <button
                            class="w-full text-left px-4 py-2 text-sm text-theme-base hover:bg-theme-card disabled:opacity-40 disabled:cursor-not-allowed"
                            :disabled="
                                effectiveCurrentPage === effectiveTotalPages
                            "
                            @click="goToNextPage"
                        >
                            ▶ Enviar a siguiente página
                        </button>
                    </li>
                    <li>
                        <button
                            class="w-full text-left px-4 py-2 text-sm text-theme-base hover:bg-theme-card disabled:opacity-40 disabled:cursor-not-allowed"
                            :disabled="
                                effectiveCurrentPage === effectiveTotalPages
                            "
                            @click="goToLastPage"
                        >
                            ⏭ Enviar a última página
                        </button>
                    </li>
                    <li class="border-t border-brand-blue mx-2 my-1"></li>
                    <li>
                        <button
                            class="w-full text-left px-4 py-2 text-sm text-theme-base hover:bg-theme-card"
                            @click="openCustomPageModal"
                        >
                            📌 Enviar a página personalizada
                        </button>
                    </li>
                    <li class="border-t border-brand-blue mx-2 my-1"></li>
                    <li class="px-4 py-1 text-xs text-theme-muted">
                        Página {{ effectiveCurrentPage }} de
                        {{ effectiveTotalPages }}
                    </li>
                </fragment>
            </ul>
        </div>

        <app-dialog v-model="customPageModal.visible" size="sm">
            <template #header>
                <h3 class="text-base font-semibold text-theme-base">
                    Enviar a página personalizada
                </h3>
            </template>
            <div>
                <p class="text-sm text-theme-muted mb-3">
                    Selecciona la página destino (1 -
                    {{ effectiveTotalPages }})
                </p>
                <input
                    v-model.number="customPageModal.targetPage"
                    type="number"
                    :min="1"
                    :max="effectiveTotalPages"
                    class="w-full border border-brand-blue rounded px-3 py-2 text-sm text-theme-base bg-transparent"
                />
                <div class="flex gap-2 justify-end mt-4">
                    <app-button flat @click="customPageModal.visible = false">
                        Cancelar
                    </app-button>
                    <app-button @click="confirmCustomPage"> Enviar </app-button>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import ClickOutside from "vue-click-outside";
import { Container, Draggable } from "vue-smooth-dnd";

import { applyDrag } from "@/common/lib/drag";

import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "CustomSortableTable",
    components: { AppDialog, Container, Draggable },
    directives: {
        ClickOutside
    },
    events: [
        "flex-selected",
        "sort-changed",
        "drag-drop",
        "row-send-to-page",
        "row-action"
    ],
    props: {
        fields: {
            type: Array,
            required: true
            // Format: [{ key, label, sortable, thClass, tdClass }]
        },
        items: {
            type: Array,
            required: true
        },
        responsive: { type: Boolean, default: true },
        selectable: { type: Boolean, default: false },
        outlined: {
            type: Boolean,
            default: false
        },
        selectMode: {
            type: String,
            default: "single",
            validator: (v) => ["single", "multi"].includes(v)
        },
        perPage: { type: Number, default: 0 },
        sortByProp: { type: String, default: null },
        sortDescProp: { type: Boolean, default: false },
        itemsEmptyMessage: { type: String, default: "" },
        draggable: { type: Boolean, default: false },
        externalCurrentPage: { type: Number, default: null },
        externalTotalPages: { type: Number, default: null },
        rowActions: { type: Array, default: () => [] }
    },
    data() {
        return {
            selectedIndex: [],
            selectedRows: [],
            sortBy: this.sortByProp,
            sortDesc: this.sortDescProp,
            localItems: [],
            currentPage: 1,
            contextMenu: {
                visible: false,
                x: 0,
                y: 0,
                rowIndex: null,
                item: null
            },
            customPageModal: {
                visible: false,
                targetPage: 1,
                item: null
            }
        };
    },
    computed: {
        effectiveCurrentPage() {
            return this.externalCurrentPage !== null
                ? this.externalCurrentPage
                : this.currentPage;
        },
        effectiveTotalPages() {
            return this.externalTotalPages !== null
                ? this.externalTotalPages
                : this.totalPages;
        },
        totalPages() {
            if (this.perPage === 0) return 1;
            return Math.max(1, Math.ceil(this.items.length / this.perPage));
        },
        paginatedItems() {
            if (this.perPage === 0) return this.items;
            const start = (this.currentPage - 1) * this.perPage;
            return this.items.slice(start, start + this.perPage);
        },
        sortedItems() {
            const items = [...this.paginatedItems];
            if (!this.sortBy) return items;
            return items.sort((a, b) => {
                const aVal = a[this.sortBy];
                const bVal = b[this.sortBy];
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                let result =
                    typeof aVal === "number" && typeof bVal === "number"
                        ? aVal - bVal
                        : String(aVal).localeCompare(String(bVal));
                return this.sortDesc ? -result : result;
            });
        }
    },
    watch: {
        items: {
            immediate: true,
            handler(val) {
                this.localItems = [...val];
                this.currentPage = 1;
            }
        }
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeyDown);
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeyDown);
    },
    methods: {
        toggleSort(fieldKey) {
            if (this.sortBy === fieldKey) {
                if (!this.sortDesc) {
                    this.sortDesc = true;
                } else {
                    this.sortBy = null;
                    this.sortDesc = false;
                }
            } else {
                this.sortBy = fieldKey;
                this.sortDesc = false;
            }
            this.$emit("sort-changed", {
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            });
        },
        getAriaSort(fieldKey) {
            if (this.sortBy !== fieldKey) return "none";
            return this.sortDesc ? "descending" : "ascending";
        },
        getSrOnlyText(fieldKey) {
            if (this.sortBy !== fieldKey) return "(Click to sort ascending)";
            if (this.sortDesc) return "(Click to clear sorting)";
            return "(Click to sort descending)";
        },
        selectRow(item, index) {
            if (!this.selectable) return;
            if (this.selectMode === "single") {
                this.selectedIndex = this.selectedIndex.includes(index)
                    ? []
                    : [index];
                this.selectedRows = this.selectedIndex.length > 0 ? [item] : [];
            } else {
                const idx = this.selectedRows.findIndex((r) => r === item);
                if (idx > -1) {
                    this.selectedRows.splice(idx, 1);
                    this.selectedIndex.splice(
                        this.selectedIndex.indexOf(index),
                        1
                    );
                } else {
                    this.selectedRows.push(item);
                    this.selectedIndex.push(index);
                }
            }
            this.$emit("flex-selected", this.selectedRows);
        },
        clearSelected() {
            this.selectedIndex = [];
            this.selectedRows = [];
        },
        onDrop(dropResult) {
            this.localItems = applyDrag([...this.localItems], dropResult);
            this.$emit("drag-drop", [...this.localItems]);
        },
        openContextMenu(event, item, index) {
            const menuWidth = 200;
            const menuHeight = 180;
            const x =
                event.clientX + menuWidth > window.innerWidth
                    ? event.clientX - menuWidth
                    : event.clientX;
            const y =
                event.clientY + menuHeight > window.innerHeight
                    ? event.clientY - menuHeight
                    : event.clientY;
            this.contextMenu = {
                visible: true,
                x,
                y,
                rowIndex: index,
                item: item || null
            };
            if (!this.draggable && index !== undefined) {
                this.selectedIndex = [index];
                this.selectedRows = item !== undefined ? [item] : [];
                this.$emit("flex-selected", this.selectedRows);
            }
        },
        closeContextMenu() {
            this.contextMenu.visible = false;
            this.contextMenu.rowIndex = null;
            this.contextMenu.item = null;
        },
        handleKeyDown(event) {
            if (event.key === "Escape") {
                this.closeContextMenu();
            }
        },
        goToFirstPage() {
            this.setPage(1);
        },
        goToLastPage() {
            this.setPage(this.effectiveTotalPages);
        },
        goToNextPage() {
            this.setPage(
                Math.min(
                    this.effectiveCurrentPage + 1,
                    this.effectiveTotalPages
                )
            );
        },
        goToPrevPage() {
            this.setPage(Math.max(this.effectiveCurrentPage - 1, 1));
        },
        setPage(page) {
            const item = this.contextMenu.item;
            if (this.externalCurrentPage === null) {
                this.currentPage = page;
            }
            this.closeContextMenu();
            this.$emit("row-send-to-page", { item, page });
        },
        emitRowAction(actionKey, item) {
            this.$emit("row-action", {
                action: actionKey,
                item,
                selected: [...this.selectedRows]
            });
        },
        openCustomPageModal() {
            this.customPageModal.item = this.contextMenu.item;
            this.customPageModal.targetPage = this.effectiveCurrentPage;
            this.customPageModal.visible = true;
            this.closeContextMenu();
        },
        confirmCustomPage() {
            const page = parseInt(this.customPageModal.targetPage);
            if (!page || page < 1 || page > this.effectiveTotalPages) return;
            const item = this.customPageModal.item;
            this.customPageModal.visible = false;
            this.$emit("row-send-to-page", { item, page });
        }
    }
};
</script>

<style scoped>
.sort-icons {
    @apply inline-flex opacity-30 text-xs transition-opacity duration-200;
}
.sortable-header:hover .sort-icons {
    @apply opacity-60;
}
.sort-icons.active {
    @apply opacity-100;
}
</style>
